import {
  BuildingOffice2Icon,
  MapPinIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import { providerDirectoryV2Api } from '@/services/api/providerDirectoryV2Service';
import type { ProviderDirectoryV2Service } from '@/services/types/provider-directory-v2-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Provider service tooltip content component
 * Shows service name, type, and clinical location on hover.
 */
export const ProviderServiceTooltipContent = memo<
  EntityTooltipContentProps<ProviderDirectoryV2Service>
>(({ fetchState }) => {
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="provider service" />;
  }

  if (fetchState.status === 'idle') {
    return <EmptyState entityType="provider service" />;
  }

  const service = fetchState.data;
  const { name, service_type, clinical_location } = service;

  const locationName = clinical_location?.name;
  const orgName = clinical_location?.organization?.name;
  const address = clinical_location?.address?.formatted_address;

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader icon={BuildingOffice2Icon} title={name || 'Provider Service'} />

      <TooltipSection withBorder>
        {service_type && (
          <InfoRow icon={TagIcon} label="Type" value={service_type} />
        )}
        {(orgName || locationName) && (
          <InfoRow
            icon={BuildingOffice2Icon}
            label="Location"
            value={[orgName, locationName].filter(Boolean).join(' - ')}
          />
        )}
        {address && (
          <InfoRow icon={MapPinIcon} label="Address" value={address} />
        )}
      </TooltipSection>
    </div>
  );
});

ProviderServiceTooltipContent.displayName = 'ProviderServiceTooltipContent';

/**
 * Hoverable provider service link with tooltip preview
 *
 * Shows provider service name, type, and clinical location on hover.
 * Used in sync suggestion cards to display provider directory references.
 *
 * @example
 * ```tsx
 * <ProviderServiceLink entityId={serviceId}>
 *   Provider Directory Link
 * </ProviderServiceLink>
 * ```
 */
export const ProviderServiceLink = createEntityLink<
  ProviderDirectoryV2Service,
  { serviceId: string }
>({
  tooltipContent: ProviderServiceTooltipContent,
  getDetailUrl: null,
  useQuery:
    providerDirectoryV2Api.endpoints.getProviderDirectoryService.useQuery,
  getQueryArg: (id) => ({ serviceId: id }),
  displayName: 'ProviderServiceLink',
});
